// crm-activity.ts — TRACK C: the authoritative CRM ACTIVITY record shape + pure builders/validators.
//
// A CRM console needs a truthful timeline of what happened across the account: a contact added, a deal
// moved to Won, an order placed, a booking requested. This module defines the ONE record shape that
// timeline reads from — (type, actor identity, timestamp, ref) — plus pure helpers to build, validate,
// derive (from a deal transition), sort, and label an activity. It is framework-free and dependency-
// free so both the gateway append handler and the creator timeline share exactly one definition.
//
// HONESTY (the whole point): normalizeCrmActivity REJECTS a malformed row (returns null) rather than
// inventing fields, so the timeline can only ever render REAL, well-formed, persisted rows — never a
// fabricated event and never a partially-guessed one. Timestamps are epoch-ms numbers injected by the
// caller (server-authoritative in the append handler; the shared layer never calls Date.now).
//
// COLLECTION: the base collection is `activity` (a generic cross-category log — orders, bookings,
// deals, contacts). Under the CRM composite the compositor namespaces it to `crm-activity` (the same
// `crm-` secondary-slug prefix that turns `deals` → `crm-deals`), which baseCollectionName() resolves
// back to `activity` on the owner route. So a standalone CRM app reads `/owner/activity` and a
// composite reads `/owner/crm-activity`; both hit the one `activity` contract.

/** The kinds of event a CRM timeline records. Cross-category: order / booking / deal / contact + a
 *  free-form owner note. Extend deliberately — an unknown type is rejected by normalizeCrmActivity. */
export type CrmActivityType =
  | 'contact_created'
  | 'contact_updated'
  | 'contact_archived'
  | 'contact_restored'
  | 'deal_created'
  | 'deal_stage_changed'
  | 'deal_won'
  | 'deal_lost'
  | 'deal_archived'
  | 'order_placed'
  | 'order_status_changed'
  | 'booking_requested'
  | 'booking_status_changed'
  | 'note_added'
  // The ONLY type a client may author via the /append endpoint. Every other type is a SYSTEM event
  // emitted server-side from a real business mutation — a browser can never forge one (see the append
  // handler). A manual_note always carries user_authored:true.
  | 'manual_note';

export const CRM_ACTIVITY_TYPES: readonly CrmActivityType[] = [
  'contact_created',
  'contact_updated',
  'contact_archived',
  'contact_restored',
  'deal_created',
  'deal_stage_changed',
  'deal_won',
  'deal_lost',
  'deal_archived',
  'order_placed',
  'order_status_changed',
  'booking_requested',
  'booking_status_changed',
  'note_added',
  'manual_note',
];

/** The subset of activity types that are SYSTEM-AUTHORITATIVE (emitted server-side from a business
 *  mutation). A client-authored entry may NEVER be one of these — the append handler forces manual_note. */
export const CRM_SYSTEM_ACTIVITY_TYPES: readonly CrmActivityType[] = CRM_ACTIVITY_TYPES.filter(
  (t) => t !== 'manual_note',
);

/** Who performed the event. `id` is the stable identity (creator account id / customer end-user id)
 *  when known; `label` is an honest display name (never fabricated — omitted when unknown). */
export type CrmActorKind = 'owner' | 'customer' | 'system';
export interface CrmActor {
  kind: CrmActorKind;
  id?: string;
  label?: string;
}

/** The row an activity points at (a deal / contact / order / booking). `collection` + `id` are the
 *  authoritative reference; `label` is a human string (deal title / contact name) for display. */
export interface CrmActivityRef {
  collection: string;
  id: string;
  label?: string;
}

/** One immutable, append-only activity record. */
export interface CrmActivity {
  /** server-assigned row id — absent before persist. */
  id?: string;
  type: CrmActivityType;
  actor: CrmActor;
  /** epoch milliseconds (injected — server-authoritative on append). */
  at: number;
  ref?: CrmActivityRef;
  /** for *_stage_changed / *_status_changed — the prior and new state. */
  from?: string;
  to?: string;
  /** optional owner-authored note. */
  note?: string;
  /** true ONLY for a manual_note the owner typed via the /append endpoint — never set on a system event,
   *  so a client-authored row can never masquerade as an authoritative system event. */
  user_authored?: boolean;
}

/** The base owner-managed collection the activity log persists to (namespaced `crm-activity` under
 *  the CRM composite). */
export const CRM_ACTIVITY_COLLECTION = 'activity';
/** The namespaced form the CRM composite exposes on the owner route. */
export const CRM_ACTIVITY_NAMESPACED = 'crm-activity';

const ACTOR_KINDS: readonly CrmActorKind[] = ['owner', 'customer', 'system'];

export function isCrmActivityType(x: unknown): x is CrmActivityType {
  return typeof x === 'string' && (CRM_ACTIVITY_TYPES as readonly string[]).includes(x);
}

export function isCrmActorKind(x: unknown): x is CrmActorKind {
  return typeof x === 'string' && (ACTOR_KINDS as readonly string[]).includes(x);
}

function coerceActor(x: unknown): CrmActor | null {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return null;
  const a = x as Record<string, unknown>;
  if (!isCrmActorKind(a.kind)) return null;
  const actor: CrmActor = { kind: a.kind };
  if (typeof a.id === 'string' && a.id) actor.id = a.id;
  if (typeof a.label === 'string' && a.label) actor.label = a.label;
  return actor;
}

function coerceRef(x: unknown): CrmActivityRef | undefined {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return undefined;
  const r = x as Record<string, unknown>;
  if (typeof r.collection !== 'string' || !r.collection) return undefined;
  if (typeof r.id !== 'string' || !r.id) return undefined;
  const ref: CrmActivityRef = { collection: r.collection, id: r.id };
  if (typeof r.label === 'string' && r.label) ref.label = r.label;
  return ref;
}

/** Validate + normalize an arbitrary (server) row into a CrmActivity, or return null when it is not a
 *  well-formed activity. HONEST: a row missing a valid type, actor.kind, or numeric timestamp is
 *  REJECTED (null) — the timeline drops it rather than rendering a fabricated/partial event. `id` is
 *  preserved when present so the caller can key the row. */
export function normalizeCrmActivity(x: unknown): CrmActivity | null {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return null;
  const o = x as Record<string, unknown>;
  if (!isCrmActivityType(o.type)) return null;
  const actor = coerceActor(o.actor);
  if (!actor) return null;
  const at = typeof o.at === 'number' && Number.isFinite(o.at) ? o.at : Number.NaN;
  if (!Number.isFinite(at)) return null;
  const out: CrmActivity = { type: o.type, actor, at };
  if (typeof o.id === 'string' && o.id) out.id = o.id;
  const ref = coerceRef(o.ref);
  if (ref) out.ref = ref;
  if (typeof o.from === 'string' && o.from) out.from = o.from;
  if (typeof o.to === 'string' && o.to) out.to = o.to;
  if (typeof o.note === 'string' && o.note) out.note = o.note;
  if (o.user_authored === true) out.user_authored = true;
  return out;
}

export interface MakeCrmActivityInput {
  type: CrmActivityType;
  actor: CrmActor;
  at: number;
  ref?: CrmActivityRef;
  from?: string;
  to?: string;
  note?: string;
  user_authored?: boolean;
}

/** Build a validated CrmActivity, or null if the inputs are not well-formed (invalid type/actor/at).
 *  Used by the append handler to produce the exact persisted shape. */
export function makeCrmActivity(input: MakeCrmActivityInput): CrmActivity | null {
  return normalizeCrmActivity(input as unknown);
}

/** Derive the activity for a deal STAGE transition: Won/Lost map to the specific terminal event; any
 *  other advance is a generic stage change carrying from → to. Actor + timestamp are recorded. */
export function crmActivityForDealTransition(
  ref: CrmActivityRef,
  actor: CrmActor,
  from: string,
  to: string,
  at: number
): CrmActivity {
  const type: CrmActivityType = to === 'Won' ? 'deal_won' : to === 'Lost' ? 'deal_lost' : 'deal_stage_changed';
  return { type, actor, at, ref, from, to };
}

/** Newest-first sort by timestamp (stable for equal timestamps). Does not mutate the input. */
export function sortCrmActivities(list: readonly CrmActivity[]): CrmActivity[] {
  return [...list].sort((a, b) => b.at - a.at);
}

/** The i18n key for an activity's headline (`shop.crm.act.<type>`); the caller falls back to
 *  summarizeCrmActivity when the key is absent. */
export function crmActivityTitleKey(a: Pick<CrmActivity, 'type'>): string {
  return `shop.crm.act.${a.type}`;
}

/** A plain-English, non-fabricated one-line summary used as the display fallback. Uses only fields the
 *  record actually carries (ref label, from/to) — never invents detail. */
export function summarizeCrmActivity(a: CrmActivity): string {
  const subject = a.ref?.label || a.ref?.id || '';
  const base: Record<CrmActivityType, string> = {
    contact_created: 'Customer added',
    contact_updated: 'Customer updated',
    contact_archived: 'Customer archived',
    contact_restored: 'Customer restored',
    deal_created: 'Deal created',
    deal_stage_changed: 'Deal stage changed',
    deal_won: 'Deal won',
    deal_lost: 'Deal lost',
    deal_archived: 'Deal archived',
    order_placed: 'Order placed',
    order_status_changed: 'Order status changed',
    booking_requested: 'Booking requested',
    booking_status_changed: 'Booking status changed',
    note_added: 'Note added',
    manual_note: 'Note added',
  };
  let line = base[a.type];
  if (subject) line += ` — ${subject}`;
  if (a.type === 'deal_stage_changed' && a.from && a.to) line += ` (${a.from} → ${a.to})`;
  return line;
}
